import type {PropertyDefinitionBase, PropertyUsagesResponse, TargetLiterals} from '@github-ui/custom-properties-types'
import {useMutation, useQuery} from '@github-ui/react-query'
import sudo from '@github-ui/sudo'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {Dialog, type DialogButtonProps} from '@primer/react'
import {type RefObject, useState} from 'react'

import {DefinitionUsageBanner} from './DefinitionUsageBanner'
import {ServerErrorFormBanner} from './ServerErrorFormBanner'

type Props = {
  definition: PropertyDefinitionBase
  deletePath: string
  usagePath: string
  targetLiterals: TargetLiterals
  returnFocusRef?: RefObject<HTMLElement | null>
  onCancel: () => void
  onSuccess: () => void
}

export function DeleteDefinitionDialog({
  definition,
  deletePath,
  usagePath,
  targetLiterals,
  returnFocusRef,
  onCancel,
  onSuccess,
}: Props) {
  const [deleting, setDeleting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const deleteMutation = useMutation({
    mutationFn: deleteDefinition,
    onMutate: async () => {
      setDeleting(true)
      setErrorMessage('')

      if (!(await sudo())) throw new Error('Unauthorized')
    },
    onSuccess,
    onError: error => {
      setErrorMessage(error.message)
      setDeleting(false)
    },
  })

  const {
    data: usages,
    isLoading: usagesLoading,
    error: usagesError,
  } = useQuery<PropertyUsagesResponse>({
    queryKey: ['definitions-usage-results', definition.propertyName, usagePath],
    queryFn: async () => checkPropertyUsage(usagePath),
  })

  // Escape focus trap when sudo dialog is open
  if (deleting) return null

  return (
    <Dialog
      width="small"
      onClose={onCancel}
      title="Delete property"
      returnFocusRef={returnFocusRef}
      renderBody={() => (
        <>
          {errorMessage && (
            <div aria-live="polite" className="pl-3 pr-3 pt-2">
              <ServerErrorFormBanner>{errorMessage}</ServerErrorFormBanner>
            </div>
          )}
          <div>
            <div className="p-3">
              {usagesLoading ? (
                <span>Checking usages...</span>
              ) : (
                <DefinitionUsageBanner
                  propertyName={definition.propertyName}
                  count={usages?.count}
                  targetLiterals={targetLiterals}
                  errorMessage={usagesError?.message}
                />
              )}
            </div>
            <div className="pl-3 pr-3 pb-3">
              <span>
                This will permanently delete this property. <strong>This action cannot be undone.</strong>
              </span>
            </div>
          </div>
        </>
      )}
      footerButtons={[
        {
          onClick: onCancel,
          content: 'Cancel',
        },
        ...(!usagesLoading
          ? [
              {
                buttonType: 'danger',
                onClick: () => deleteMutation.mutate(deletePath),
                content: deleting ? 'Deleting...' : 'Delete',
                'aria-disabled': deleting,
              } as DialogButtonProps,
            ]
          : []),
      ]}
    />
  )
}

const deleteDefinition = async (path: string) => {
  const result = await reactFetchJSON(path, {method: 'DELETE'})
  if (result.ok) return

  throw new Error(`Something went wrong.`)
}

const checkPropertyUsage = async (usagePath: string) => {
  const result = await reactFetchJSON(usagePath)
  if (result.ok) return await result.json()

  throw new Error(`Failed to load property usage.`)
}
